import {
  CalculatedPromotions,
  CalculatePromotionsPayload,
  PromotionProvidersResponse,
} from '@chargeafter/payment-types';
import { asyncDebounce } from '@chargeafter/utils';

import {
  calculateMonthlyPayment,
  formatDescriptionPlaceholders,
} from './utils';

import { logger } from '../../logger';
import { getPromotions } from '../getPromotions/getPromotions';
import { printInitError } from '../printInitError/printInitError';
import { store } from '../store';

const filename = 'calculate';

const UPDATE_DEBOUNCE_TIME = 500;

const hasPromotionCategory = (
  metadata: Record<string, unknown>
): metadata is { categories: string[] } => {
  return (
    metadata &&
    'categories' in metadata &&
    Array.isArray(metadata.categories) &&
    metadata.categories.length > 0
  );
};

const filterProviders = (
  providers: PromotionProvidersResponse['providers'],
  { amount, lenderId }: CalculatePromotionsPayload
): PromotionProvidersResponse['providers'] => {
  return providers.reduce((acc, provider) => {
    if (lenderId && provider.lenderId !== lenderId) {
      return acc;
    }

    const promotions = provider.promotions.filter((promotion) => {
      const eligibility = promotion?.eligibility;
      const activation = promotion?.activation;
      const terms = promotion?.terms;
      const dateNow = Date.now();

      const overMaximumAmount =
        typeof eligibility?.maximumPurchaseAmount === 'number' &&
        eligibility.maximumPurchaseAmount < Number(amount);

      const bellowMinimumAmount =
        typeof eligibility?.minimumPurchaseAmount === 'number' &&
        eligibility.minimumPurchaseAmount > Number(amount);

      const notActive =
        activation &&
        (!activation?.active ||
          (activation?.startDate &&
            new Date(activation.startDate).getTime() > dateNow) ||
          (activation?.endDate &&
            new Date(activation.endDate).getTime() < dateNow));

      return terms && !overMaximumAmount && !bellowMinimumAmount && !notActive;
    });

    if (promotions.length > 0) {
      acc.push({ ...provider, promotions });
    }

    return acc;
  }, []);
};

const debouncedCalculate = asyncDebounce(
  async (
    context: CalculatePromotionsPayload
  ): Promise<CalculatedPromotions | undefined> => {
    if (!context) {
      logger.error('context is required', {
        filename,
        function: 'calculate',
      });

      return undefined;
    }

    logger.log(`calculating promotions`, {
      function: 'calculate',
      filename,
      context,
    });

    const { amount, filterByCategories, excludeCategories } = context;

    if (!amount) {
      logger.error('amount is required', {
        function: 'calculate',
        filename,
      });

      return undefined;
    }

    const response = await getPromotions();

    if (response) {
      const filteredProviders = filterProviders(
        response?.providers || [],
        context
      );

      return filteredProviders.reduce((acc, provider) => {
        const lenderId = provider.lenderId;

        const promotions = provider.promotions.reduce((acc, promotion) => {
          const {
            terms,
            disclosures,
            description,
            promoType: promotionType,
            financialProductType,
            activation,
            metadata,
            eligibility,
          } = promotion;

          // this indicate if we need to filter out the promotion by some category rules
          const hasCategoriesInPromotions = hasPromotionCategory(metadata);

          const isPromotionOutOfCategory =
            filterByCategories?.length > 0 &&
            (!hasCategoriesInPromotions ||
              (hasCategoriesInPromotions &&
                !metadata.categories.some((category) => {
                  return (
                    filterByCategories
                      ?.map((filterCategory) => filterCategory.toLowerCase())
                      .includes((category as string)?.toLowerCase()) || false
                  );
                })));

          const isExcludePromotionByCategory =
            excludeCategories?.length > 0 &&
            hasCategoriesInPromotions &&
            excludeCategories.some((category) => {
              const isCategoryExist = metadata.categories
                ?.map((category) => category.toLowerCase())
                .includes((category as string)?.toLowerCase());

              return (
                isCategoryExist &&
                !filterByCategories
                  ?.map((filterCategory) => filterCategory.toLowerCase())
                  .includes((category as string)?.toLowerCase())
              );
            });

          if (isPromotionOutOfCategory || isExcludePromotionByCategory) {
            return acc;
          }

          const monthlyPayment = calculateMonthlyPayment({
            amount,
            ...terms,
          });

          const formattedDescription =
            typeof description === 'string' &&
            formatDescriptionPlaceholders({
              description,
              amount,
              monthlyPayment,
              numberOfPeriods: terms?.numberOfPeriods,
              minimumPurchaseAmount: eligibility?.minimumPurchaseAmount,
              startDate: activation?.startDate,
              endDate: activation?.endDate,
            });

          const disclosuresEntries = Object.entries(disclosures || {});

          const formattedDisclosure =
            disclosuresEntries.length > 0
              ? {
                  ...disclosuresEntries[0][1],
                  value: formatDescriptionPlaceholders({
                    description: disclosuresEntries[0][1].value || '',
                    amount,
                    monthlyPayment,
                    numberOfPeriods: terms?.numberOfPeriods,
                    minimumPurchaseAmount: eligibility?.minimumPurchaseAmount,
                    startDate: activation?.startDate,
                    endDate: activation?.endDate,
                  }),
                }
              : null;

          const calculatedPromotion: CalculatedPromotions[0] = {
            lenderId,
            promotion: {
              ...terms,
              ...(metadata && { metadata }),
              ...(description && { description: formattedDescription }),
              ...(activation && {
                activation: {
                  ...(activation.startDate && {
                    startDate: activation.startDate,
                  }),
                  ...(activation.endDate && { endDate: activation.endDate }),
                },
              }),
              disclosure: formattedDisclosure,
              monthlyPayment,
              promotionType,
              financialProductType,
            },
          };

          return [...acc, calculatedPromotion];
        }, []);

        return [...acc, ...promotions];
      }, []);
    }

    return [];
  },
  UPDATE_DEBOUNCE_TIME
);

export const calculate = async (args: CalculatePromotionsPayload) => {
  if (!store.isInitialized) {
    printInitError('calculate', filename);
    return undefined;
  }

  return await debouncedCalculate(args);
};
